import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchActiveLanguages,
  fetchTranslations,
  setSelectedLanguage,
} from "../store/language/action";
import { baseURL } from "../util/Config";
import "./LanguageSwitcher.css";

// ─── Read URL param directly (no useSearchParams needed) ─────────────────────
// Safe to use outside BrowserRouter since it reads window.location directly.
const getUrlLangParam = () => {
  try {
    return (
      new URLSearchParams(window.location.search)
        .get("language")
        ?.trim()
        ?.toLowerCase() || null
    );
  } catch {
    return null;
  }
};

const LanguageSwitcher = () => {
  const dispatch = useDispatch();
  const {
    activeLanguages,
    selectedLanguageCode,
    selectedLanguage,
    loadingTranslations,
  } = useSelector((state) => state.language);

  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const urlLangParam = getUrlLangParam();

  // Fetch active languages on mount
  // Reducer will resolve correct language from URL → localStorage → default
  useEffect(() => {
    dispatch(fetchActiveLanguages());
  }, [dispatch]);

  // Fetch translations whenever selectedLanguageCode changes
  useEffect(() => {
    if (!selectedLanguageCode) return;
    dispatch(fetchTranslations(selectedLanguageCode));
  }, [selectedLanguageCode, dispatch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (code) => {
    if (code?.toLowerCase() === selectedLanguageCode?.toLowerCase()) {
      setOpen(false);
      return;
    }
    dispatch(setSelectedLanguage(code)); // saves to localStorage + updates Redux
    setOpen(false);
  };

  const buildIconSrc = (iconPath) => {
    if (!iconPath) return null;
    if (/^https?:\/\//i.test(iconPath)) return iconPath;
    return `${baseURL || ""}${iconPath}`;
  };

  const currentLabel =
    selectedLanguage?.localLanguageTitle ||
    selectedLanguage?.languageTitle ||
    selectedLanguageCode?.toUpperCase() ||
    "EN";

  const currentFlagSrc = buildIconSrc(selectedLanguage?.languageIcon);

  return (
    <div className="lang-switcher-wrapper" ref={dropdownRef}>
      {/* Dropdown — opens upward */}
      {open && (
        <div className="lang-dropdown" role="menu" aria-label="Select language">
          <div className="lang-dropdown-header">Select Language</div>

          {activeLanguages?.length ? (
            activeLanguages.map((lang) => {
              const code = lang.languageCode?.toLowerCase();
              const isActive = code === selectedLanguageCode?.toLowerCase();
              const isFromUrl = isActive && urlLangParam === code;
              const iconSrc = buildIconSrc(lang.languageIcon);
              const label = lang.localLanguageTitle || lang.languageTitle;

              return (
                <button
                  key={lang._id || lang.languageCode}
                  className={`lang-option${isActive ? " active" : ""}`}
                  onClick={() => handleSelect(lang.languageCode)}
                  type="button"
                >
                  {iconSrc ? (
                    <img
                      src={iconSrc}
                      alt={label || lang.languageCode}
                      className="lang-flag"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="lang-flag-placeholder">
                      {lang.languageCode?.slice(0, 2)}
                    </div>
                  )}
                  <span className="lang-name">{label}</span>
                  {isFromUrl && <span className="lang-url-tag">URL</span>}
                  <span className="lang-code-badge">{lang.languageCode}</span>
                  {isActive && <span className="lang-check">✓</span>}
                </button>
              );
            })
          ) : (
            <div className="lang-empty">Loading languages...</div>
          )}
        </div>
      )}

      {/* Trigger button */}
      <button
        className={`lang-trigger${open ? " open" : ""}${urlLangParam ? " url-sourced" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        title="Switch language"
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <svg
          className="lang-globe-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>

        {currentFlagSrc ? (
          <img
            src={currentFlagSrc}
            alt={currentLabel}
            className="lang-flag"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : null}

        <span>{currentLabel}</span>

        {loadingTranslations ? (
          <span className="lang-loading-dot" aria-hidden="true" />
        ) : (
          <svg
            className={`lang-chevron${open ? " rotated" : ""}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="18 15 12 9 6 15" />
          </svg>
        )}
      </button>
    </div>
  );
};

export default LanguageSwitcher;
